import { useState } from "react";
import { Box, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { FormikProps } from "formik";
import { COUNTRIES_LIST, STATE_LIST } from "../../../../../constants";
import {
  ADDRESS_INITIAL_VALUES,
  NewPatientForm,
} from "../../../../../constants/provider/patient/add-new-patient/add-new-patient-form";
import CustomSelect from "../../../../CustomSelect";
import Flex from "../../../../Flex";
import Input from "../../../../Input";
import MaskInput from "../../../../MaskInput";
import OutlineCard from "../../../../OutlineCard";
import Text from "../../../../Text";
import CustomTitle from "../../../CustomTitle";
import NumberInput from "../../../../NumberInput";

interface ContactInformationProps {
  formik: FormikProps<NewPatientForm>;
}

const ContactInformation = ({ formik }: ContactInformationProps) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    formik;
  const [mobileFocused, setMobileFocused] = useState(false);

  const setEmContactDetail = (k: string, v?: string) => {
    const ind = values?.emergencyContacts?.findIndex(
      (e) => e?.relationshipWithPatient === "SELF"
    );

    if (ind !== undefined) setFieldValue(`emergencyContacts[${ind}].${k}`, v);
  };

  const setInsuranceDetail = (k: string, v?: string) => {
    const ind = values?.patientInsurances?.findIndex(
      (e) => e?.insuredRelationshipWithPatient === "SELF"
    );

    if (ind !== undefined) setFieldValue(`patientInsurances[${ind}].${k}`, v);
  };

  return (
    <Box
      id={"Contact Information"}
      sx={{
        mt: 1,
      }}
    >
      <Flex justifyContent={"space-between"} alignItems={"center"}>
        <CustomTitle mb={1}>Contact Information</CustomTitle>

        <FormControlLabel
          control={
            <Checkbox
              checked={!!values?.isScriptSure}
              onChange={async (e) => {
                const checked = e.target.checked;
                await setFieldValue("isScriptSure", checked, false);
                if (!checked) {
                  await setFieldValue("address", ADDRESS_INITIAL_VALUES, false);
                }
                formik.validateForm();
              }}
            />
          }
          label={<Text>Add patient to ScriptSure</Text>}
        />
      </Flex>
      <OutlineCard>
        <Grid
          container
          spacing={3}
          sx={{
            p: 0.8,
          }}
        >
          <Grid size={3}>
            <Flex flexDirection={"column"}>
              <MaskInput
                mask={"(999) 999-9999"}
                label="Mobile Number"
                disabled={values?.phoneNotAvailable}
                name="mobileNumber"
                onBlur={(e) => {
                  setMobileFocused(false);
                  handleBlur(e);
                }}
                onFocus={() => setMobileFocused(true)}
                // placeholder="Enter Number"
                value={values.mobileNumber}
                onChange={(e) => {
                  setFieldValue("mobileNumber", e.target.value);
                  setInsuranceDetail("subscriberMobileNumber", e.target.value);
                }}
                required={!values?.phoneNotAvailable}
                error={
                  !mobileFocused &&
                  touched?.mobileNumber &&
                  errors?.mobileNumber
                }
              />

              <Flex mt={"10px"}>
                <FormControlLabel
                  value="end"
                  sx={{ alignItems: "flex-start", m: 0 }}
                  control={
                    <Checkbox
                      name="phoneNotAvailable"
                      size="small"
                      checked={values?.phoneNotAvailable}
                      onChange={(e) => {
                        if (e.target.checked)
                          formik.setFieldValue("mobileNumber", "");

                        formik.setFieldTouched("mobileNumber", true);
                        handleChange(e);
                      }}
                      sx={{ p: 0, mr: "6px", mt: "1px" }}
                    />
                  }
                  label={
                    <Text fontSize={"12px"}>
                      Patient doesn't have a mobile phone
                    </Text>
                  }
                  labelPlacement="end"
                />
              </Flex>
            </Flex>
          </Grid>
          <Grid size={3}>
            <MaskInput
              mask={"(999) 999-9999"}
              label="Home Number"
              placeholder="Enter Number"
              value={values.homePhone}
              onChange={(e) => {
                setFieldValue("homePhone", e.target.value);
                setEmContactDetail("mobile", e.target.value);
              }}
            />
          </Grid>
          <Grid size={3}>
            <Flex flexDirection={"column"}>
              <Input
                required={!values?.emailNotAvailable}
                label="Email"
                textFieldProps={{
                  name: "email",
                  disabled: values?.emailNotAvailable,
                  value: values.email ?? "",
                  onChange: (e) => {
                    handleChange(e);
                    setEmContactDetail("email", e.target.value);
                  },
                  onFocus: () => formik.setFieldTouched("email", false, false),
                  onBlur: handleBlur,
                  // placeholder: "Enter Email",
                }}
                error={touched?.email && errors?.email}
              />
              <Flex mt={"10px"}>
                <FormControlLabel
                  value="end"
                  sx={{ alignItems: "flex-start", m: 0 }}
                  control={
                    <Checkbox
                      name="emailNotAvailable"
                      size="small"
                      checked={values?.emailNotAvailable}
                      onChange={(e) => {
                        if (e.target.checked)
                          formik.setFieldValue("email", undefined);

                        formik.setFieldTouched("email", true);
                        handleChange(e);
                      }}
                      sx={{ p: 0, mr: "6px", mt: "1px" }}
                    />
                  }
                  label={
                    <Text fontSize={"12px"}>
                      Patient doesn't have an email address
                    </Text>
                  }
                  labelPlacement="end"
                />
              </Flex>
            </Flex>
          </Grid>
          <Grid size={3}>
            <NumberInput
              label="Fax Number"
              textFieldProps={{
                name: "faxNumber",
                value: values.faxNumber,
                onChange: handleChange,
                onBlur: handleBlur,
                // placeholder: "Enter Fax Number",
              }}
              // required
              // error={touched.faxNumber && errors.faxNumber}
            />
          </Grid>

          <Grid size={6}>
            <Input
              label="Address Line 1"
              textFieldProps={{
                name: "address.line1",
                value: values.address?.line1 || "",
                onChange: (e) => {
                  setFieldValue("address.line1", e.target.value, true);
                  setInsuranceDetail("subscriberAddress.line1", e.target.value);
                },
                onBlur: handleBlur,
                // placeholder: "Enter Address Line 1",
              }}
              required={!!values?.isScriptSure}
              error={
                touched.address && ((errors.address as any)?.line1 as string)
              }
            />
          </Grid>
          <Grid size={6}>
            <Input
              label="Address Line 2"
              textFieldProps={{
                name: "address.line2",
                value: values.address?.line2 || "",
                onChange: (e) => {
                  setFieldValue("address.line2", e.target.value);
                  setInsuranceDetail("subscriberAddress.line2", e.target.value);
                },
                onBlur: handleBlur,
                // placeholder: "Enter Address Line 2",
              }}
            />
          </Grid>
          <Grid size={3}>
            <Input
              label="City"
              textFieldProps={{
                name: "address.city",
                value: values.address?.city || "",
                onChange: (e) => {
                  setFieldValue("address.city", e.target.value);
                  setInsuranceDetail("subscriberAddress.city", e.target.value);
                },
                onBlur: handleBlur,
                // placeholder: "Enter City",
              }}
              required={!!values?.isScriptSure}
              error={
                touched?.address && ((errors?.address as any)?.city as string)
              }
            />
          </Grid>
          <Grid size={3}>
            <CustomSelect
              label="State"
              value={values?.address?.state || undefined}
              options={STATE_LIST}
              onChange={(value) => {
                setFieldValue("address.state", value, true);
                setInsuranceDetail("subscriberAddress.state", value as any);
              }}
              required={!!values?.isScriptSure}
              error={
                touched?.address && ((errors?.address as any)?.state as string)
              }
              // placeholder="Select State"
            />
          </Grid>
          <Grid size={3}>
            <CustomSelect
              value={values.address?.country || undefined}
              disableSortOptions
              options={COUNTRIES_LIST}
              onChange={(value) => {
                setFieldValue("address.country", value);
                setInsuranceDetail("subscriberAddress.country", value as any);
              }}
              required={!!values?.isScriptSure}
              error={
                touched?.address &&
                ((errors?.address as any)?.country as string)
              }
              label="Country"
              // placeholder="Select Country"
            />
          </Grid>
          <Grid size={3}>
            <Input
              label="ZIP Code"
              textFieldProps={{
                name: "address.zipcode",
                value: (() => {
                  const d = (values.address?.zipcode || "")
                    .replace(/\D/g, "")
                    .slice(0, 9);
                  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
                })(),
                onChange: (e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 9);
                  const val =
                    digits.length > 5
                      ? `${digits.slice(0, 5)}-${digits.slice(5)}`
                      : digits;
                  setFieldValue("address.zipcode", val);
                  setInsuranceDetail("subscriberAddress.zipcode", val);
                },
                onBlur: handleBlur,
                inputProps: { maxLength: 10 },
              }}
              required={!!values?.isScriptSure}
              error={
                touched?.address && ((errors.address as any)?.zipcode as string)
              }
            />
          </Grid>
        </Grid>
      </OutlineCard>
    </Box>
  );
};

export default ContactInformation;
